// #tag: VAULT DOCUMENT DOWNLOAD AND EXPORT PIPELINE
// #purpose: Prepares student documents for export, converting formats (PDF, JPEG, PNG, WebP), applying dimension presets (passport, signature), and compressing file sizes.
import {
  imageBlobToPdf,
  imageBlobToTargetSizePdf,
  pdfBlobToTargetSizePdf,
  pdfFirstPageToBlob,
} from './pdfConvert';

export type VaultDownloadFormat = 'original' | 'pdf' | 'jpeg' | 'png' | 'webp';
export type VaultDownloadSize = 'original' | 'passport' | 'signature' | 'compact' | 'standard';

export interface VaultDownloadOptions {
  format: VaultDownloadFormat;
  size: VaultDownloadSize;
  quality: number;
  /** Max file size in KB when exporting as PDF. Empty/null = original / best quality. */
  pdfTargetSizeKb: number | null;
}

export interface PrepareVaultDownloadProgress {
  percent: number;
  label?: string;
}

export const DOWNLOAD_FORMAT_OPTIONS: Array<{ id: VaultDownloadFormat; label: string; hint?: string }> = [
  { id: 'original', label: 'Original format', hint: 'Keep the uploaded file as-is' },
  { id: 'pdf', label: 'PDF', hint: 'Portable document format' },
  { id: 'jpeg', label: 'JPEG', hint: 'Best for photos and online forms' },
  { id: 'png', label: 'PNG', hint: 'Lossless — good for signatures' },
  { id: 'webp', label: 'WebP', hint: 'Smaller file size' },
];

export const DOWNLOAD_SIZE_OPTIONS: Array<{ id: VaultDownloadSize; label: string; hint: string }> = [
  { id: 'original', label: 'Original size', hint: 'No resizing' },
  { id: 'passport', label: 'Passport photo', hint: '413 × 531 px (3.5 × 4.5 cm @ 300 DPI)' },
  { id: 'signature', label: 'Signature', hint: '400 × 120 px strip' },
  { id: 'compact', label: 'Compact', hint: 'Max 800 px — ~50–100 KB target' },
  { id: 'standard', label: 'Standard', hint: 'Max 1200 px — ~100–200 KB target' },
];

const SIZE_PRESETS: Record<
  Exclude<VaultDownloadSize, 'original'>,
  { width?: number; height?: number; maxWidth?: number; maxHeight?: number; fit?: 'cover' | 'contain' }
> = {
  passport: { width: 413, height: 531, fit: 'cover' },
  signature: { width: 400, height: 120, fit: 'contain' },
  compact: { maxWidth: 800, maxHeight: 800, fit: 'contain' },
  standard: { maxWidth: 1200, maxHeight: 1200, fit: 'contain' },
};

function sanitizeFileName(name: string): string {
  return name.replace(/[^\w\s.-]/g, '').trim().replace(/\s+/g, '-') || 'document';
}

function extensionForFormat(format: VaultDownloadFormat, sourceMime?: string): string {
  if (format === 'original') {
    if (sourceMime === 'application/pdf') return 'pdf';
    if (sourceMime === 'image/png') return 'png';
    if (sourceMime === 'image/webp') return 'webp';
    if (sourceMime === 'image/jpeg') return 'jpg';
    return 'bin';
  }
  if (format === 'pdf') return 'pdf';
  if (format === 'jpeg') return 'jpg';
  return format;
}

function mimeForFormat(format: VaultDownloadFormat, sourceMime?: string): string {
  if (format === 'original') return sourceMime || 'application/octet-stream';
  if (format === 'pdf') return 'application/pdf';
  if (format === 'jpeg') return 'image/jpeg';
  if (format === 'png') return 'image/png';
  return 'image/webp';
}

function resolveCanvasSize(
  sourceWidth: number,
  sourceHeight: number,
  size: VaultDownloadSize,
): { width: number; height: number; fit: 'cover' | 'contain' } {
  if (size === 'original') {
    return { width: sourceWidth, height: sourceHeight, fit: 'contain' };
  }

  const preset = SIZE_PRESETS[size];
  if (preset.width && preset.height) {
    return { width: preset.width, height: preset.height, fit: preset.fit ?? 'contain' };
  }

  const maxWidth = preset.maxWidth ?? sourceWidth;
  const maxHeight = preset.maxHeight ?? sourceHeight;
  const scale = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight, 1);
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
    fit: preset.fit ?? 'contain',
  };
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image file'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Export failed'))),
      mimeType,
      quality,
    );
  });
}

function parsePdfTargetBytes(pdfTargetSizeKb: number | null | undefined): number | null {
  if (pdfTargetSizeKb == null || !Number.isFinite(pdfTargetSizeKb) || pdfTargetSizeKb <= 0) return null;
  return Math.round(pdfTargetSizeKb * 1024);
}

export function parseTargetSizeKbInput(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed);
}

/** Fast size hint — never runs PDF compression. */
export function getDownloadSizeHint(
  sourceBlob: Blob,
  sourceMime: string | undefined,
  options: VaultDownloadOptions,
): string {
  const mime = sourceMime || sourceBlob.type;
  const isPdf = mime === 'application/pdf';

  if (options.format === 'original' && options.size === 'original') {
    return formatBytes(sourceBlob.size);
  }

  if (options.format === 'pdf') {
    if (options.pdfTargetSizeKb) return `Up to ${options.pdfTargetSizeKb} KB`;
    if (isPdf) return formatBytes(sourceBlob.size);
    return 'Optimized on download';
  }

  if (options.format === 'jpeg' || options.format === 'webp') {
    return 'Varies with quality setting';
  }

  return 'Calculated on download';
}

/** Whether preview needs a lightweight transform (image formats only). */
export function needsTransformPreview(options: VaultDownloadOptions): boolean {
  return options.format === 'jpeg' || options.format === 'png' || options.format === 'webp';
}

async function transformImageBlob(blob: Blob, options: VaultDownloadOptions): Promise<Blob> {
  const isOriginal = options.format === 'original' && options.size === 'original';
  if (isOriginal) return blob;

  const img = await loadImage(blob);
  const target = resolveCanvasSize(img.naturalWidth, img.naturalHeight, options.size);
  const canvas = document.createElement('canvas');
  canvas.width = target.width;
  canvas.height = target.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const scale = target.fit === 'cover'
    ? Math.max(target.width / img.naturalWidth, target.height / img.naturalHeight)
    : Math.min(target.width / img.naturalWidth, target.height / img.naturalHeight);

  const drawWidth = img.naturalWidth * scale;
  const drawHeight = img.naturalHeight * scale;
  const dx = (target.width - drawWidth) / 2;
  const dy = (target.height - drawHeight) / 2;

  ctx.drawImage(img, dx, dy, drawWidth, drawHeight);

  const outputFormat = options.format === 'original' || options.format === 'pdf'
    ? (blob.type.startsWith('image/') ? blob.type : 'image/jpeg')
    : mimeForFormat(options.format);

  const quality = outputFormat === 'image/png' ? undefined : options.quality;
  return canvasToBlob(canvas, outputFormat, quality);
}

async function sourceAsImageBlob(blob: Blob, sourceMime: string | undefined): Promise<Blob> {
  const mime = sourceMime || blob.type;
  if (mime.startsWith('image/')) return blob;
  if (mime === 'application/pdf' || blob.type === 'application/pdf') {
    return pdfFirstPageToBlob(blob);
  }
  throw new Error('This file type cannot be converted to an image.');
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export async function prepareVaultDownload(
  blob: Blob,
  documentName: string,
  sourceMime: string | undefined,
  options: VaultDownloadOptions,
  onProgress?: (progress: PrepareVaultDownloadProgress) => void,
): Promise<{ blob: Blob; fileName: string; sizeLabel: string }> {
  const mime = sourceMime || blob.type;
  const isPdf = mime === 'application/pdf' || blob.type === 'application/pdf';
  const isImage = mime.startsWith('image/');

  if (options.format === 'original' && options.size === 'original') {
    const ext = extensionForFormat('original', mime);
    onProgress?.({ percent: 100 });
    return {
      blob,
      fileName: `${sanitizeFileName(documentName)}.${ext}`,
      sizeLabel: formatBytes(blob.size),
    };
  }

  if (options.format === 'pdf') {
    const targetBytes = parsePdfTargetBytes(options.pdfTargetSizeKb);
    onProgress?.({ percent: 8, label: 'Preparing PDF' });

    if (isPdf) {
      if (!targetBytes || blob.size <= targetBytes) {
        onProgress?.({ percent: 100 });
        return {
          blob,
          fileName: `${sanitizeFileName(documentName)}.pdf`,
          sizeLabel: formatBytes(blob.size),
        };
      }
      const compressed = await pdfBlobToTargetSizePdf(blob, targetBytes, (percent) => {
        onProgress?.({ percent, label: 'Compressing PDF' });
      });
      return {
        blob: compressed,
        fileName: `${sanitizeFileName(documentName)}.pdf`,
        sizeLabel: formatBytes(compressed.size),
      };
    }

    const imageBlob = isImage ? blob : await sourceAsImageBlob(blob, mime);
    if (!targetBytes) {
      onProgress?.({ percent: 40, label: 'Building PDF' });
      const pdfBlob = await imageBlobToPdf(imageBlob);
      onProgress?.({ percent: 100 });
      return {
        blob: pdfBlob,
        fileName: `${sanitizeFileName(documentName)}.pdf`,
        sizeLabel: formatBytes(pdfBlob.size),
      };
    }

    const compressed = await imageBlobToTargetSizePdf(imageBlob, targetBytes, (percent) => {
      onProgress?.({ percent, label: 'Compressing PDF' });
    });
    return {
      blob: compressed,
      fileName: `${sanitizeFileName(documentName)}.pdf`,
      sizeLabel: formatBytes(compressed.size),
    };
  }

  onProgress?.({ percent: 30, label: 'Processing image' });
  const imageBlob = isImage ? blob : isPdf ? await sourceAsImageBlob(blob, mime) : null;
  if (!imageBlob) {
    throw new Error('Format and size options are only available for image and PDF files.');
  }

  const output = await transformImageBlob(imageBlob, options);
  const ext =
    output.type === 'image/jpeg' ? 'jpg'
    : output.type === 'image/png' ? 'png'
    : output.type === 'image/webp' ? 'webp'
    : extensionForFormat(options.format, mime);

  onProgress?.({ percent: 100 });
  return {
    blob: output,
    fileName: `${sanitizeFileName(documentName)}.${ext}`,
    sizeLabel: formatBytes(output.size),
  };
}

export function defaultDownloadOptions(_sourceMime?: string): VaultDownloadOptions {
  return {
    format: 'original',
    size: 'original',
    quality: 0.85,
    pdfTargetSizeKb: null,
  };
}

export function supportsDownloadTransforms(sourceMime?: string): boolean {
  if (!sourceMime) return false;
  return sourceMime.startsWith('image/') || sourceMime === 'application/pdf';
}
